import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileSpreadsheet, Plus, Loader2, Trash2, Building2, X } from "lucide-react";

const money = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const STATUS = {
  aberta: "bg-chart-3/12 text-chart-3 border-chart-3/25",
  faturada: "bg-warning/15 text-warning border-warning/30",
  paga: "bg-success/12 text-success border-success/25",
  glosada: "bg-destructive/12 text-destructive border-destructive/25",
};

export default function Tiss() {
  const [insurers, setInsurers] = useState([]);
  const [patients, setPatients] = useState([]);
  const [tuss, setTuss] = useState([]);
  const [guides, setGuides] = useState([]);
  const [insOpen, setInsOpen] = useState(false);
  const [insForm, setInsForm] = useState({ name: "", ans_code: "", plan: "" });
  const [gOpen, setGOpen] = useState(false);
  const [g, setG] = useState({ patient_id: "", insurer_id: "", guide_type: "consulta", date: new Date().toISOString().slice(0, 10) });
  const [procs, setProcs] = useState([]);
  const [tussSel, setTussSel] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/insurers").then((r) => setInsurers(r.data));
    api.get("/tiss-guides").then((r) => setGuides(r.data));
  };
  useEffect(() => {
    load();
    api.get("/patients").then((r) => setPatients(r.data));
    api.get("/tuss").then((r) => setTuss(r.data));
  }, []);

  const seedDefaults = async () => {
    try {
      const { data } = await api.post("/insurers/seed-defaults");
      toast.success(data.added > 0 ? `${data.added} convênio(s) adicionados` : "Convênios padrão já estavam cadastrados");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao carregar convênios"); }
  };

  const saveInsurer = async () => {
    if (!insForm.name.trim()) { toast.error("Informe o nome do convênio"); return; }
    await api.post("/insurers", insForm);
    toast.success("Convênio cadastrado");
    setInsOpen(false); setInsForm({ name: "", ans_code: "", plan: "" }); load();
  };
  const removeInsurer = async (id) => { await api.delete(`/insurers/${id}`); load(); };

  const addProc = () => {
    const t = tuss.find((x) => x.code === tussSel);
    if (!t) return;
    setProcs([...procs, { tuss_code: t.code, description: t.description, value: t.value, qty: 1 }]);
    setTussSel("");
  };
  const total = procs.reduce((s, p) => s + p.value * p.qty, 0);

  const saveGuide = async () => {
    if (!g.patient_id || !g.insurer_id) { toast.error("Selecione paciente e convênio"); return; }
    if (procs.length === 0) { toast.error("Adicione ao menos um procedimento"); return; }
    setSaving(true);
    try {
      await api.post("/tiss-guides", { ...g, procedures: procs });
      toast.success("Guia TISS gerada");
      setGOpen(false); setProcs([]); setG({ ...g, patient_id: "", insurer_id: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const setStatus = async (gid, status) => { await api.put(`/tiss-guides/${gid}/status?status=${status}`); load(); };
  const removeGuide = async (gid) => { await api.delete(`/tiss-guides/${gid}`); load(); };

  return (
    <div className="p-6 sm:p-10 max-w-6xl">
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-ai mb-2">Convênios</p>
        <h1 className="font-heading text-4xl font-light tracking-tighter">Faturamento TISS / TUSS</h1>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl font-medium flex items-center gap-2"><Building2 className="w-5 h-5" /> Convênios</h2>
        <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={seedDefaults} data-testid="seed-insurers-btn"><Plus className="w-4 h-4 mr-2" /> Carregar padrão</Button>
        <Dialog open={insOpen} onOpenChange={setInsOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" data-testid="add-insurer-btn"><Plus className="w-4 h-4 mr-2" /> Convênio</Button>
          </DialogTrigger>
          <DialogContent data-testid="insurer-dialog">
            <DialogHeader><DialogTitle>Cadastrar convênio</DialogTitle>
              <DialogDescription>Operadora e plano.</DialogDescription></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Nome</Label>
                <Input value={insForm.name} onChange={(e) => setInsForm({ ...insForm, name: e.target.value })} data-testid="insurer-name-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Registro ANS</Label>
                  <Input value={insForm.ans_code} onChange={(e) => setInsForm({ ...insForm, ans_code: e.target.value })} data-testid="insurer-ans-input" /></div>
                <div className="space-y-2"><Label>Plano</Label>
                  <Input value={insForm.plan} onChange={(e) => setInsForm({ ...insForm, plan: e.target.value })} data-testid="insurer-plan-input" /></div>
              </div>
            </div>
            <DialogFooter><Button onClick={saveInsurer} data-testid="save-insurer-btn">Salvar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      {insurers.length === 0 ? (
        <Card className="p-6 text-center text-muted-foreground border-dashed mb-8">Nenhum convênio cadastrado.</Card>
      ) : (
        <div className="grid sm:grid-cols-3 gap-3 mb-8">
          {insurers.map((i) => (
            <Card key={i.id} className="p-4 border-border shadow-none flex items-center justify-between" data-testid={`insurer-${i.id}`}>
              <div>
                <p className="text-sm font-medium">{i.name}</p>
                <p className="text-xs text-muted-foreground">{i.plan || "—"}{i.ans_code ? ` · ANS ${i.ans_code}` : ""}</p>
              </div>
              <Button size="icon" variant="ghost" onClick={() => removeInsurer(i.id)} data-testid={`delete-insurer-${i.id}`}>
                <Trash2 className="w-4 h-4 text-muted-foreground" />
              </Button>
            </Card>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl font-medium flex items-center gap-2"><FileSpreadsheet className="w-5 h-5" /> Guias TISS</h2>
        <Dialog open={gOpen} onOpenChange={setGOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-guide-btn"><Plus className="w-4 h-4 mr-2" /> Nova guia</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="guide-dialog">
            <DialogHeader><DialogTitle>Gerar guia TISS</DialogTitle>
              <DialogDescription>Selecione procedimentos da tabela TUSS.</DialogDescription></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Paciente</Label>
                  <Select value={g.patient_id} onValueChange={(v) => setG({ ...g, patient_id: v })}>
                    <SelectTrigger data-testid="guide-patient-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Convênio</Label>
                  <Select value={g.insurer_id} onValueChange={(v) => setG({ ...g, insurer_id: v })}>
                    <SelectTrigger data-testid="guide-insurer-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>{insurers.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Tipo</Label>
                  <Select value={g.guide_type} onValueChange={(v) => setG({ ...g, guide_type: v })}>
                    <SelectTrigger data-testid="guide-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="consulta">Consulta</SelectItem>
                      <SelectItem value="sp_sadt">SP/SADT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Data</Label>
                  <Input type="date" value={g.date} onChange={(e) => setG({ ...g, date: e.target.value })} data-testid="guide-date-input" /></div>
              </div>
              <div className="border-t border-border pt-3 space-y-2">
                <Label>Adicionar procedimento (TUSS)</Label>
                <div className="flex gap-2">
                  <Select value={tussSel} onValueChange={setTussSel}>
                    <SelectTrigger data-testid="guide-tuss-select"><SelectValue placeholder="Procedimento" /></SelectTrigger>
                    <SelectContent>
                      {tuss.map((t) => <SelectItem key={t.code} value={t.code}>{t.code} · {t.description} ({money(t.value)})</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={addProc} data-testid="add-proc-btn"><Plus className="w-4 h-4" /></Button>
                </div>
                {procs.map((p, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-secondary/50 rounded-md p-2 text-sm">
                    <span>{p.tuss_code} · {p.description}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono-data">{money(p.value)}</span>
                      <button onClick={() => setProcs(procs.filter((_, i) => i !== idx))} data-testid={`remove-proc-${idx}`}><X className="w-4 h-4 text-muted-foreground" /></button>
                    </div>
                  </div>
                ))}
                {procs.length > 0 && <p className="text-right font-medium">Total: {money(total)}</p>}
              </div>
            </div>
            <DialogFooter>
              <Button onClick={saveGuide} disabled={saving} data-testid="save-guide-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Gerar guia
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {guides.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground border-dashed">Nenhuma guia gerada.</Card>
      ) : (
        <div className="space-y-3">
          {guides.map((gd) => (
            <Card key={gd.id} className="p-5 border-border shadow-none" data-testid={`guide-${gd.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="font-medium">{gd.patient_name} <span className="text-muted-foreground font-normal">· {gd.insurer_name}</span></p>
                  <p className="text-xs text-muted-foreground">Guia {gd.guide_number} · {gd.guide_type.toUpperCase()} · {gd.date} · {gd.procedures.length} proc.</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono-data font-semibold">{money(gd.total)}</span>
                  <Select value={gd.status} onValueChange={(v) => setStatus(gd.id, v)}>
                    <SelectTrigger className="w-32" data-testid={`guide-status-${gd.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["aberta", "faturada", "paga", "glosada"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Badge variant="outline" className={STATUS[gd.status]}>{gd.status}</Badge>
                  <Button size="icon" variant="ghost" onClick={() => removeGuide(gd.id)} data-testid={`delete-guide-${gd.id}`}>
                    <Trash2 className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
